
import { Injectable } from '@angular/core';
import { StarmsClientProvider } from '../starms-client/starms-client';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CustomerAdapter, Customer } from '../../app/models/Customer';
import { SiteAdapter, Site } from '../../app/models/Site';
import { Train, TrainAdapter } from '../../app/models/Train';
import { Entity } from '../../app/models/Entity';

export interface EntitiesStatus {
  customers: boolean;
  sites: boolean;
  trains: boolean;
}

@Injectable()
export class EntitiesProvider {

  customers: Customer[] = [];
  sites: Site[] = [];
  trains: Train[] = [];

  entitiesStatus = new BehaviorSubject<EntitiesStatus>({
    customers: false,
    sites: false,
    trains: false
  });
  statusSubscriptions: Subscription[] = [];
  customersSubscription: Subscription;
  sitesSubscription: Subscription;
  trainsSubscription: Subscription;
  currentEntity: Entity;
  errorsChanges = new BehaviorSubject<number>(0);

  private addError() {
    const value = this.errorsChanges.getValue();
    this.errorsChanges.next(value + 1);
  }

  public resetErrors() {
    this.errorsChanges.next(0);
  }


  private searchPredicate = (item: Entity, query: string) => {
    if (item.getName()) {
      return (item.getName().toLowerCase().indexOf(query.toLowerCase()) > -1);
    }
    return false;
  }


  constructor(private starms: StarmsClientProvider) {
  }

  /**
   * Trigger entity fetch and return fetch status
   */
  fetchEntities(): BehaviorSubject<EntitiesStatus> {
    this.getCustomers();
    this.getSites();
    this.getTrains();
    return this.entitiesStatus;
  }

  getCustomers() {
    if (this.customersSubscription) {
      this.customersSubscription.unsubscribe();
    }

    this.customersSubscription = this.starms.get(['customers']).map((item: any[]) => item.map(customer => new CustomerAdapter().adapt(customer))
    ).subscribe(customers => {
      this.customers = customers;

      this.updateStatus('customers');
    }, error => {
      
      if (error.status === 500 || error.status === 0) {

        this.addError();
        this.retry('customers');
      }
    });
  }

  getSites() {
    if (this.sitesSubscription) {
      this.sitesSubscription.unsubscribe();
    }

    this.sitesSubscription = this.starms.get(['sites']).map(item => item.map(site => new SiteAdapter().adapt(site))).subscribe(sites => {
      this.sites = sites;

      this.updateStatus('sites');
    }, error => {
  
      if (error.status === 500 || error.status === 0) {
        this.addError();
        this.retry('sites');
      }
    });
  }

  getTrains() {
    if (this.trainsSubscription) {
      this.trainsSubscription.unsubscribe();
    }

    this.trainsSubscription = this.starms.get(['trains'])
      .map(item => item.map(train => new TrainAdapter().adapt(train))).subscribe(trains => {
        this.trains = trains;
        this.updateStatus('trains');
      }, error => {
        console.log('Trains fetch error ', error);
        if (error.status === 500 || error.status === 0) {
          this.addError();
          this.retry('trains');
        }
      });
  }

  retry(dataType: string) {
    if (this.errorsChanges.getValue() < 6) {
      if (dataType === 'customers') {
        this.getCustomers();
      } else if (dataType === 'sites') {
        this.getSites();
      } else if (dataType === 'trains') {
        this.getTrains();
      }
    }
  }

  getTrainStatus(customerId: number): Subscription[] {
    const trains = this.getTrainsByCustomer(customerId);
    const subscriptions = [];
    for (let train of trains) {
      train.lastConnection.status = 'updating';
      let sub = this.starms.get(['trains', train.getId(), 'status']).subscribe(trainStatus => {
        train.lastConnection.timestamp = trainStatus.lastConnection;
        train.lastConnection.status = 'done';
      }, () => {
        train.lastConnection.status = 'error';
      });
      this.statusSubscriptions.push(sub);
    }
    return subscriptions;
  }

  updateStatus(type: 'customers' | 'sites' | 'trains') {
    let status = this.entitiesStatus.getValue();
    status[type] = true;
    this.entitiesStatus.next(status);
  }

  addSites() {
    for (let customer of this.customers) {
      customer.sites = this.sites.filter(site => site.customerId === customer.getId());
    }
  }

  addTrains() {
    for (let customer of this.customers) {
      for (let site of customer.sites) {
        site.trains = this.trains.filter(train => train.siteId === site.getId()).map(train => {
          return train;
        });
      }
    }
  }

  /**
   * Clear entities data
   * customers, sites, trains, current entity
   * reset entities gathering status
   */
  clear() {
    this.customers = [];
    this.sites = [];
    this.trains = [];
    this.currentEntity = null;
  }

  resetFetchStatus() {
    this.entitiesStatus.next({
      customers: false,
      trains: false,
      sites: false
    });
  }

  resetExpanded() {
    this.customers = this.customers.map(customer => {
      customer.expanded = false;
      return customer;
    });
  }

  search(input: string): Customer[] {
    this.resetExpanded();

    return this.customers.filter(customer => {
      let siteSearch = customer.sites.filter(site => this.searchPredicate(site, input)).length;
      let trainSearch = customer.sites.filter(site =>
        site.trains.filter(train => this.searchPredicate(train, input)).length > 0).length;
      let customerSearch = this.searchPredicate(customer, input);
      if (siteSearch || customerSearch || trainSearch) {
        return true;
      }
      return false;
    }).map(customer => {
      customer.expanded = true;
      customer.sites = customer.sites.map(site => {
        site.expanded = true;
        return site;
      });
      return customer;
    });
  }

  getTrainsByCustomer(customerId: number): Train[] {
    const customer = this.getCustomer(customerId);
    if (customer) {
      const mergedTrains: Train[] = [];
      customer.sites.forEach(site => mergedTrains.push(...site.trains));
      return mergedTrains;
    }
    return null;
  }

  getCustomer(customerId: number): Customer {
    return this.customers.filter(customer => customer.getId() === customerId)[0];
  }

  unsubscribeTrainStats() {
    for (let sub of this.statusSubscriptions) {
      sub.unsubscribe();
    }
    this.statusSubscriptions = [];
  }

  clearSubscriptions() {
    if (this.customersSubscription) {
      this.customersSubscription.unsubscribe();
    }
    if (this.sitesSubscription) {
      this.sitesSubscription.unsubscribe();
    }
    if (this.trainsSubscription) {
      this.trainsSubscription.unsubscribe();
    }
    this.unsubscribeTrainStats();
  }
}



// WEBPACK FOOTER //
// ./src/providers/entities/entities.ts